import * as React from "react";
import { cn } from "@/lib/utils";
import { X, CheckCircle, XCircle, AlertTriangle, Info } from "lucide-react";
import { AppIcon } from "./AppIcon";
import { IconGhostButton } from "./button";

/**
 * NotificationService - Centralized Notification System (MANDATORY)
 * 
 * Per Global UI Standardization Enforcement - Notifications System:
 * - Success → Green toast top-right
 * - Error → Red toast top-right
 * - Warning → Orange toast
 * - Info → Blue toast
 * - Auto dismiss: 4 seconds
 * - Manual close icon required
 * 
 * Usage:
 * ```tsx
 * const { notify } = useNotificationService();
 * notify.success("Data saved successfully");
 * notify.error("Failed to save", correlationId);
 * ```
 */

type NotificationType = "success" | "error" | "warning" | "info";

interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  correlationId?: string;
  duration: number;
  createdAt: number;
}

interface NotificationServiceContextType {
  notify: {
    success: (message: string) => void;
    error: (message: string, correlationId?: string) => void;
    warning: (message: string) => void;
    info: (message: string) => void;
  };
}

const NotificationServiceContext = React.createContext<NotificationServiceContextType | null>(null);

const DEFAULT_DURATION = 4000; // 4 seconds auto-dismiss
const ERROR_DEDUP_WINDOW_MS = 2000; // suppress duplicate error messages within 2 s

const typeConfig = {
  success: {
    bgColor: "bg-success",
    icon: CheckCircle,
    iconColor: "text-card",
  },
  error: {
    bgColor: "bg-destructive",
    icon: XCircle,
    iconColor: "text-card",
  },
  warning: {
    bgColor: "bg-warning-soft0",
    icon: AlertTriangle,
    iconColor: "text-card",
  },
  info: {
    bgColor: "bg-sky-600",
    icon: Info,
    iconColor: "text-card",
  },
};

interface NotificationItemProps {
  notification: Notification;
  onDismiss: (id: string) => void;
}

function NotificationItem({ notification, onDismiss }: NotificationItemProps) {
  const config = typeConfig[notification.type];
  const Icon = config.icon;

  React.useEffect(() => {
    if (notification.duration > 0) {
      const timer = setTimeout(() => {
        onDismiss(notification.id);
      }, notification.duration);
      return () => clearTimeout(timer);
    }
  }, [notification.id, notification.duration, onDismiss]);

  return (
    <div
      className={cn(
        "flex items-start gap-3 w-full max-w-sm p-3 rounded-lg shadow-lg text-card",
        "animate-in slide-in-from-right-5 fade-in-0 duration-200",
        config.bgColor
      )}
      role="alert"
    >
      <AppIcon Icon={Icon} size="default" className={cn("flex-shrink-0 mt-0.5", config.iconColor)} />
      
      <div className="flex-1 min-w-0">
        <p className="app-body-text font-medium">{notification.message}</p>
        {notification.type === "error" && notification.correlationId && (
          <p className="app-small-meta text-card/80 mt-0.5">
            Correlation ID: {notification.correlationId}
          </p>
        )}
      </div>

      <IconGhostButton
        icon={X}
        tooltip="Dismiss notification"
        onClick={() => onDismiss(notification.id)}
        className="min-h-0 h-7 w-7 flex-shrink-0 rounded text-card hover:bg-card/20 hover:text-card"
      />
    </div>
  );
}

export function NotificationServiceProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  const lastErrorRef = React.useRef<{ message: string; timestamp: number }>({ message: '', timestamp: 0 });

  const addNotification = React.useCallback(
    (type: NotificationType, message: string, correlationId?: string, duration = DEFAULT_DURATION) => {
      // Dedup: suppress identical error messages arriving within a short window
      // (e.g. interceptor + component catch block both firing for the same failure).
      if (type === 'error') {
        const now = Date.now();
        if (
          message === lastErrorRef.current.message &&
          now - lastErrorRef.current.timestamp < ERROR_DEDUP_WINDOW_MS
        ) {
          return; // duplicate – skip
        }
        lastErrorRef.current = { message, timestamp: now };
      }

      const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const notification: Notification = {
        id,
        type,
        message,
        correlationId,
        duration,
        createdAt: Date.now(),
      };
      setNotifications((prev) => [...prev, notification]);
    },
    []
  );

  // Register the global handler so non-React code (e.g. axios interceptor)
  // can show toasts via NotificationService.error() / .success() etc.
  React.useEffect(() => {
    setGlobalNotificationHandler(addNotification);
    return () => { globalAddNotification = null; };
  }, [addNotification]);

  const dismissNotification = React.useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const notify = React.useMemo(
    () => ({
      success: (message: string) => addNotification("success", message),
      error: (message: string, correlationId?: string) => addNotification("error", message, correlationId),
      warning: (message: string) => addNotification("warning", message),
      info: (message: string) => addNotification("info", message),
    }),
    [addNotification]
  );

  const contextValue = React.useMemo(() => ({ notify }), [notify]);

  return (
    <NotificationServiceContext.Provider value={contextValue}>
      {children}
      
      {/* Notification container - top-right per Global UI */}
      <div
        className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none"
        aria-live="polite"
        aria-label="Notifications"
      >
        {notifications.map((notification) => (
          <div key={notification.id} className="pointer-events-auto">
            <NotificationItem
              notification={notification}
              onDismiss={dismissNotification}
            />
          </div>
        ))}
      </div>
    </NotificationServiceContext.Provider>
  );
}

/**
 * Hook to access notification service
 * 
 * Usage:
 * ```tsx
 * const { notify } = useNotificationService();
 * notify.success("Data saved successfully");
 * notify.error("Failed to save", response.correlationId);
 * notify.warning("Please review your input");
 * notify.info("Processing...");
 * ```
 */
export function useNotificationService() {
  const context = React.useContext(NotificationServiceContext);
  if (!context) {
    throw new Error("useNotificationService must be used within NotificationServiceProvider");
  }
  return context;
}

/**
 * Standalone notification service for non-React contexts
 * (Use sparingly - prefer hook in React components)
 */
let globalAddNotification: ((type: NotificationType, message: string, correlationId?: string) => void) | null = null;

export function setGlobalNotificationHandler(
  handler: (type: NotificationType, message: string, correlationId?: string) => void
) {
  globalAddNotification = handler;
}

export const NotificationService = {
  success: (message: string) => globalAddNotification?.("success", message),
  error: (message: string, correlationId?: string) => globalAddNotification?.("error", message, correlationId),
  warning: (message: string) => globalAddNotification?.("warning", message),
  info: (message: string) => globalAddNotification?.("info", message),
};

export default NotificationServiceProvider;

